import { of as observableOf, of } from 'rxjs';

import {
  filter,
  share,
  withLatestFrom,
  switchMap,
  catchError,
  map,
  mergeMap,
  debounceTime,
  exhaustMap,
} from 'rxjs/operators';
import { NetworkService } from './../../services/network.services';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { ErrorsType } from '../../enum/errors.type';
import { AppState } from '../../interfaces/app-state.interface';
import { BackendService } from '../../services/backend/backend.service';
import { FinanceActions } from './finance.action';
import { DefaultResponse } from '../../models/form/DefaultResponse';
import { RoutesTable } from '../../resources/routes-table.model';
import { MonthsEnum } from '../../enum/months.enum';
import { RealtimeService } from '../../services/realtime/realtime.service';
import { UtilitiesService } from '../../services/utilities.service';
import { RealtimeHub } from '../../enum/realtime/realtime-hub.enum';
import { RolesUser } from '../../enum/roles-user.enum';
import { FilesService } from '../../services/files/files.service';
import { StocksApiService } from '../../services/stocks/stocks-api.service';
import { ReportIncomeService } from '../../services/report-income/report-income-api.service';
import { FixedIncomeReportService } from '../../services/fixedincome/fixed-income-report.service';

@Injectable()
export class FinanceEffects {
  /**
   * Seleciona user profile da store
   */
  public userProfile$ = this.store
    .select((state) => state)
    .pipe(
      filter(
        (state) => !!state.userProfile.user && !!state.userProfile.user.profile
      )
    );

  /**
   * ?
   */
  getBrokageNoteIncomeSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FinanceActions.getBrokageNoteIncomeSuccess),
        share()
      ),
    { dispatch: false }
  );

  getBrokageNoteIncomeError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FinanceActions.getBrokageNoteIncomeError),
        share()
      ),
    { dispatch: false }
  );

  /**
   * Obtém valor disponível para investir
   */
  getFinancePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getFinancePosition),
      withLatestFrom(this.store.select((state) => state.authentication)),
      switchMap(([, store]: any) => {
        if (store.currentUser && store.currentUser.token) {
          return this.dataService
            .get<DefaultResponse>('posrisk/financeposition')
            .pipe(
              map((result) => {
                if (result.hasValue) {
                  return {
                    type: FinanceActions.getFinancePositionSuccess,
                    payload: result.value,
                  };
                }
                if (result.hasError) {
                  return {
                    type: FinanceActions.getFinancePositionError,
                    payload: {
                      type: ErrorsType.Error,
                      value: result.messages,
                    },
                  };
                }

                return {
                  type: FinanceActions.getFinancePositionError,
                  payload: {
                    type: ErrorsType.Error,
                    value: result.messages,
                  },
                };
              }),
              catchError((error) =>
                of({
                  type: FinanceActions.getFinancePositionError,
                  payload: error,
                })
              )
            );
        } else {
          return of({ type: FinanceActions.getFinancePositionStopped });
        }
      }),
      share()
    )
  );

  /**
   * Obtém valor disponível para investir
   */
  getNoteIncomeDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getNoteIncomeDate),
      switchMap(() => {
        return this.dataService
          .get<DefaultResponse>('fixedincome/incomenotedate')
          .pipe(
            map((result) => {
              if (result.hasAlerta) {
                return {
                  type: FinanceActions.getNoteIncomeDateError,
                  payload: {
                    type: ErrorsType.Warning,
                    value: result.messages,
                  },
                };
              }
              if (result.hasError) {
                return {
                  type: FinanceActions.getNoteIncomeDateError,
                  payload: {
                    type: ErrorsType.Error,
                    value: result.messages,
                  },
                };
              }
              if (result.ok && result.hasValue) {
                return {
                  type: FinanceActions.getNoteIncomeDateSuccess,
                  payload: result.value,
                };
              }

              return {
                type: FinanceActions.getNoteIncomeDateSuccess,
                payload: result,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getNoteIncomeDateError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  /**
   * Obtém valor disponível para investir
   */
  getBrokageNoteIncome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getBrokageNoteIncome),
      withLatestFrom(this.userProfile$),
      mergeMap(([action, state]: [any, any]) => {
        const transformedDate = action.payload.date.replace(/\//g, '-');
        const auth: any = state.authentication.currentUser;
        return this.fixedIncomeReportService
          .getFile(
            `/v2/reportincome/incomenote/document?typeBond=${action.payload.type}&OperationDate=${transformedDate}&Cblc=${auth.cblc}`
          )
          .pipe(
            map((result: any) => {
              const name = `nota-rf-${action.payload.date}.pdf`;
              this.networkService.downloadFile(result, name);
              return {
                type: FinanceActions.getBrokageNoteIncomeSuccess,
                payload: name,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getBrokageNoteIncomeError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  getConsolidatePosition$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getConsolidatePosition),
      withLatestFrom(this.store.select((state) => state.authentication)),
      filter(
        ([, authenticationState]: any) =>
          authenticationState.currentUser &&
          authenticationState.currentUser.token
      ),
      debounceTime(1000),
      mergeMap(([,]: any) => {
        return this.dataService
          .get<DefaultResponse>(
            RoutesTable.posRisk.routes.getConsolidatePosition.uri
          )
          .pipe(
            map((result) => {
              if (result.ok) {
                return {
                  type: FinanceActions.getConsolidatePositionSuccess,
                  payload: result.value,
                };
              }
              if (result.hasError || result.hasAlerta) {
                return {
                  type: FinanceActions.getConsolidatePositionError,
                  payload: result.messages,
                };
              }
              return {
                type: FinanceActions.getConsolidatePositionSuccess,
                payload: result.value,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getConsolidatePositionError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  getExtractDates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getExtractDates),
      mergeMap(() => {
        return this.dataService
          .get<DefaultResponse>('finance/extract/listdate')
          .pipe(
            map((result) => {
              if (result.ok) {
                return {
                  type: FinanceActions.getExtractDatesSuccess,
                  payload: result.value,
                };
              }
              return {
                type: FinanceActions.getExtractDatesError,
                payload: result.messages,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getExtractDatesError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  /**
   * Se inscreve no canal para pegar realtime de consolidated positon
   */
  getRealtimeConsolidatedPosition$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FinanceActions.getRealtimeConsolidatedPosition),
        withLatestFrom(this.userProfile$),
        filter(([action, state]: [any, any]) => {
          const hasSubscribe =
            this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
              RealtimeHub.ConsolidatedPosition
            );
          const user = state.userProfile.user;
          const auth = state.authentication.currentUser;
          return !!user && !!auth && user && auth.cblc && hasSubscribe;
        }),
        mergeMap(([action, state]: [any, any]) => {
          const user = state.userProfile.user;
          const auth = state.authentication.currentUser;

          if (user.profile !== RolesUser.Activated) {
            this.store.dispatch({
              type: FinanceActions.getRealtimeConsolidatedPositionError,
            });
            return of(null);
          }
          return this.realtimeService
            .subscribeToConsolidatedPosition(auth.cblc)
            .pipe(
              map((result) => {
                this.store.dispatch({
                  type: FinanceActions.getRealtimeConsolidatedPositionSuccess,
                  payload: this.utilsService.normalize(result),
                });
              }),
              catchError((error) => {
                this.store.dispatch({
                  type: FinanceActions.getRealtimeConsolidatedPositionError,
                  payload: error,
                });
                return of(null);
              })
            );
        }),
        share()
      ),
    { dispatch: false }
  );

  /**
   * Se inscreve no canal para pegar realtime de finance positon
   */
  getRealtimeFinancePosition$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(FinanceActions.getRealtimeFinancePosition),
        withLatestFrom(this.userProfile$),
        filter(([action, state]: [any, any]) => {
          const hasSubscribe =
            this.realtimeService.registerSubscribersAndCheckIfSubscriptionIsNeeded(
              RealtimeHub.FinancePosition
            );
          const user = state.userProfile.user;
          const auth = state.authentication.currentUser;
          return !!user && !!auth && user && auth.cblc && hasSubscribe;
        }),
        mergeMap(([action, state]: [any, any]) => {
          const user = state.userProfile.user;
          const auth = state.authentication.currentUser;

          if (user.profile !== RolesUser.Activated) {
            this.store.dispatch({
              type: FinanceActions.getRealtimeConsolidatedPositionError,
            });
            return of(null);
          }
          return this.realtimeService
            .subscribeToFinancePosition(auth.cblc)
            .pipe(
              map((result) => {
                this.store.dispatch({
                  type: FinanceActions.getRealtimeFinancePositionSuccess,
                  payload: this.utilsService.normalize(result),
                });
              }),
              catchError((error) => {
                this.store.dispatch({
                  type: FinanceActions.getRealtimeFinancePositionError,
                  payload: error,
                });
                return of(null);
              })
            );
        }),
        share()
      ),
    { dispatch: false }
  );

  handleRealtimeError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          FinanceActions.getRealtimeFinancePositionError,
          FinanceActions.getRealtimeConsolidatedPositionError
        ),
        exhaustMap((action: any) => {
          // Este effect apenas processa erros e não faz dispatch
          return of(action);
        }),
        share()
      ),
    { dispatch: false }
  );

  getExtractPDF$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getExtractPDF),
      mergeMap((action: any) => {
        return this.dataService
          .getFile(
            `finance/extract/pdf?month=${action.payload.month}&year=${action.payload.year}`
          )
          .pipe(
            map((result: any) => {
              this.networkService.downloadFile(
                result,
                `extrato-detalhado${MonthsEnum[action.payload.month]}-${
                  action.payload.year
                }.pdf`
              );
              return {
                type: FinanceActions.getExtractPDFSuccess,
                payload: result.value,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getExtractPDFError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  getReport$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getReport),
      mergeMap((action: any) => {
        const reportTypeQuery = action.payload.type
          ? `&reportType=${action.payload.type}`
          : '';
        return this.dataService
          .getFile(
            `finance/${action.payload.report}/pdf?referenceYear=${action.payload.year}${reportTypeQuery}`
          )
          .pipe(
            map((result) => {
              this.networkService.downloadFile(
                result,
                `${action.payload.filename}-${action.payload.year}.pdf`
              );
              return {
                type: FinanceActions.getReportSuccess,
                payload: result.value,
              };
            }),
            catchError((error) =>
              observableOf({
                type: FinanceActions.getReportError,
                payload: { status: error.status },
              })
            )
          );
      }),
      share()
    )
  );

  getReportByType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getReportByType),
      withLatestFrom(
        this.store.select((authStore) => authStore.authentication)
      ),
      mergeMap(([action]: [any, any]) => {
        const reportByTypeUri = `income-tax/get-income-tax-report?year=${action.payload.year}&reportType=${action.payload.type}`;

        return this.stocksApiService.get(reportByTypeUri).pipe(
          map((result: any) => {
            const data = result.data;
            if (data) {
              const fileData = FilesService.base64ToArrayBuffer(data.value);
              const fileName = `${action.payload.filename}-${action.payload.year}.pdf`;

              this.networkService.downloadFile(fileData, fileName);

              return {
                type: FinanceActions.getReportByTypeSuccess,
                payload: data.value,
              };
            } else {
              return {
                type: FinanceActions.getReportByTypeError,
                payload: 'No data received',
              };
            }
          }),
          catchError((error) =>
            of({
              type: FinanceActions.getReportByTypeError,
              payload: { status: error.status },
            })
          )
        );
      }),
      share()
    )
  );

  getSheduletransfer$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getSheduleTransfer),
      mergeMap((action: any) => {
        const url = `finance/sheduletransfer?value=${action.payload.value}&userAccountId=${action.payload.userAccountId}`;
        return this.dataService
          .post<DefaultResponse>(url, null, action.token)
          .pipe(
            map((result) => {
              if (result && result.ok) {
                return {
                  type: FinanceActions.getSheduleTransferSuccess,
                  payload: result.value,
                };
              }

              return {
                type: FinanceActions.getSheduleTransferError,
                payload: result.messages,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getSheduleTransferError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  getWithdrawHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getWithdrawHistory),
      mergeMap(() => {
        return this.dataService
          .get<DefaultResponse>('finance/withdraw/history')
          .pipe(
            map((result) => {
              if (result.ok) {
                return {
                  type: FinanceActions.getWithdrawHistorySuccess,
                  payload: result.value,
                };
              }
              return {
                type: FinanceActions.getWithdrawHistoryError,
                payload: result.messages,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getWithdrawHistoryError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  getTransferExecutionDate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getTransferExecutionDate),
      mergeMap(() => {
        return this.dataService
          .get<DefaultResponse>('finance/transfer-execution-date')
          .pipe(
            map((result) => {
              if (result.ok) {
                return {
                  type: FinanceActions.getTransferExecutionDateSuccess,
                  payload: result.value,
                };
              }
              return {
                type: FinanceActions.getTransferExecutionDateError,
                payload: result.messages,
              };
            }),
            catchError((error) =>
              of({
                type: FinanceActions.getTransferExecutionDateError,
                payload: error,
              })
            )
          );
      }),
      share()
    )
  );

  getIncomeTaxPDF$ = createEffect(() =>
    this.actions$.pipe(
      ofType(FinanceActions.getIncomeTaxPDF),
      withLatestFrom(
        this.store.select((authStore) => authStore.authentication)
      ),
      mergeMap(([action]: [any, any]) => {
        const reportByTypeUri = `report-income/${action.payload.year}/${action.payload.type}`;

        return this.reportIncomeService.get(reportByTypeUri).pipe(
          map((result: any) => {
            const data = result.data;
            if (data) {
              const fileData = FilesService.base64ToArrayBuffer(data.value);
              const fileName = `${action.payload.filename}-${action.payload.year}.pdf`;

              this.networkService.downloadFile(fileData, fileName);

              return {
                type: FinanceActions.getIncomeTaxPDFSuccess,
                payload: data.value,
              };
            } else {
              return {
                type: FinanceActions.getIncomeTaxPDFError,
                payload: { error: 'No data received' },
              };
            }
          }),
          catchError((error) =>
            of({
              type: FinanceActions.getIncomeTaxPDFError,
              payload: { status: error.status },
            })
          )
        );
      }),
      share()
    )
  );

  constructor(
    private actions$: Actions,
    private store: Store<AppState>,
    private networkService: NetworkService,
    private dataService: BackendService,
    private fixedIncomeReportService: FixedIncomeReportService,
    private realtimeService: RealtimeService,
    private utilsService: UtilitiesService,
    private stocksApiService: StocksApiService,
    private reportIncomeService: ReportIncomeService
  ) {}
}
